import { BaseMain } from '@components/Base';
import Loader from '@components/Loader';
import useAuth from '@State/Auth/useAuth';
import React, { Suspense, useEffect } from 'react';
import { Route, Routes as RouterRoutes, useLocation, useNavigate } from 'react-router-dom';
import theme from 'src/layout/theme';
import styled from 'styled-components';
import { RouteItem } from '../@types/routes';
import { privateRoutes } from './private';
import { publicRoutes } from './public';
import RedirectToLogin from './RedirectToLogin';
import ScrollToTop from './ScrollToTop';
import Page from './Shared/Page';

const StyledBaseMain = styled(BaseMain)`
  padding-top: 47px;

  ${theme.breakpoint.LtSm} {
    padding-top: 1.5rem;
  }
`;

// Router 5 prefix-matchade allt utan exact och lät en Route utan path vara
// catch-all (NotFound). I Router 7 är routes exakta om de inte slutar på /*,
// och en route utan path matchar inget - '*' ger samma catch-all, med lägst rang.
const routePath = ({ path, exact }: Pick<RouteItem, 'path' | 'exact'>) => {
  if (!path) return '*';
  return exact || path.endsWith('*') ? path : `${path}/*`;
};

const Routes = () => {
  const { authenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hashPath = window.location.hash.replace(/^#/, '');
    if (hashPath && hashPath !== location.pathname) {
      navigate(hashPath, { replace: true });
    }
  }, [navigate, location.pathname]);

  return (
    <Suspense fallback={<Loader />}>
      <ScrollToTop />
      <RouterRoutes>
        {publicRoutes.map(({ component: Component, ...props }: RouteItem) => (
          <Route
            key={`public-${props.title}`}
            path={routePath(props)}
            element={
              <Page title={props.title}>
                <Component />
              </Page>
            }
          />
        ))}

        {privateRoutes.map(({ component: Component, ...props }: RouteItem) =>
          authenticated ? (
            <Route
              key={`private-${props.title}`}
              path={routePath(props)}
              element={
                <StyledBaseMain>
                  <Page title={props.title}>
                    <Component />
                  </Page>
                </StyledBaseMain>
              }
            />
          ) : props.path ? (
            <Route
              key={`private-${props.title}`}
              path={routePath(props)}
              element={<RedirectToLogin />}
            />
          ) : null
        )}
      </RouterRoutes>
    </Suspense>
  );
};

export default Routes;
